import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import agentChatService, {abortCurrentStream} from '../data/agentChatService';
import {
  useLazyListConversationsQuery,
  useCreateConversationMutation,
  useLazyGetConversationQuery,
  useLazyGetMessagesQuery,
  useUpdateConversationMutation,
  useDeleteConversationMutation,
} from '../data/copilotV2Api';
import {getApiErrorMessage} from '../util/apiErrors';
import {useSuggestions} from './SuggestionsContext';
import {trackProductEvent} from '../util';

export const ACTIVITY_ENTRY_TYPE = {THINKING: 'thinking', TOOL: 'tool'};

const closeOpenToolEntry = (entries) => {
  const last = entries[entries.length - 1];
  if (last?.type === ACTIVITY_ENTRY_TYPE.TOOL && !last.isComplete) {
    return [...entries.slice(0, -1), {...last, isComplete: true}];
  }
  return entries;
};

const TOOL_CONFIG = {
  tiptapRead: {
    getLabels: (name) => ({
      activeLabel: name ? `Reading ${name}...` : 'Reading...',
      completeLabel: name ? `Read ${name}.` : 'Read the document.',
    }),
  },
  tiptapReadSelection: {
    getLabels: (name) => ({
      activeLabel: name ? `Reading ${name}...` : 'Reading...',
      completeLabel: name ? `Read ${name}.` : 'Read selection.',
    }),
  },
  tiptapEdit: {
    getLabels: (name) => ({
      activeLabel: name ? `Editing ${name}...` : 'Editing...',
      completeLabel: name ? `Edited ${name}.` : 'Edited the document.',
    }),
  },
};

const getSectionName = (sectionId, sections) => {
  if (!sectionId || !sections) return null;
  const section = sections.find((s) => s.id === sectionId);
  return section?.title ?? null;
};

const AgentChatContext = createContext(null);

export const AgentChatProvider = ({children, getAllEditors, documentType}) => {
  const {documentId} = useParams();
  const {refreshSuggestions} = useSuggestions();

  // Conversation state
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [failedMessage, setFailedMessage] = useState(null);

  // Streaming state
  const [currentStreamedMessage, setCurrentStreamedMessage] = useState(null);

  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [fileUploadError, setFileUploadError] = useState(null);

  // Sections from Redux — used for tool activity labels. Stored in a ref so
  // the sendMessage callback can read the current value without needing sections
  // in its dependency array (which would recreate the callback on every edit).
  const sections = useSelector((state) => state.documents?.document?.sections);
  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  // Refs
  const phaseRef = useRef('idle'); // 'idle' | 'initializing' | 'ready'
  const pendingInitialMessageRef = useRef(null); // proofread message to send after creation, used to prevent re-triggering the proofread branch when the initialMessage prop still persists on the screen
  const isCreatingConversation = useRef(false); // prevents multiple concurrent creation attempts
  const currentStreamedMessageRef = useRef(currentStreamedMessage);
  currentStreamedMessageRef.current = currentStreamedMessage;
  const toolsExecutedRef = useRef(false);

  // RTK Query hooks
  const [triggerListConversations, {isFetching: isLoadingConversations}] =
    useLazyListConversationsQuery();
  const [triggerCreateConversation] = useCreateConversationMutation();
  const [triggerGetConversation] = useLazyGetConversationQuery();
  const [triggerGetMessages] = useLazyGetMessagesQuery();
  const [triggerUpdateConversation] = useUpdateConversationMutation();
  const [triggerDeleteConversation] = useDeleteConversationMutation();

  const clearFileState = useCallback(() => {
    setUploadedFiles([]);
    setFileUploadError(null);
  }, []);

  const resortConversations = useCallback((id) => {
    setConversations((prev) => {
      const existing = prev.find((c) => c.id === id);
      if (!existing) return prev;
      const updated = {...existing, updated_at: new Date().toISOString()};
      return [updated, ...prev.filter((c) => c.id !== id)];
    });
  }, []);

  const loadConversation = useCallback(
    async (id) => {
      if (!id) return;

      setIsLoading(true);
      setError(null);
      setConversationId(id);
      isCreatingConversation.current = false;
      clearFileState();

      try {
        const data = await triggerGetMessages({conversationId: id}).unwrap();
        setMessages(data.messages || []);
        phaseRef.current = 'ready';

        trackProductEvent('Penny Conversation Loaded', {
          conversation_id: id,
          document_id: documentId,
          message_count: data.messages?.length || 0,
        });
      } catch (err) {
        console.error('Error loading conversation:', err);
        setError(getApiErrorMessage(err, {resource: 'conversation'}));
      } finally {
        setIsLoading(false);
        isCreatingConversation.current = false;
      }
    },
    [documentId, triggerGetMessages, clearFileState]
  );

  const createNewConversation = useCallback(async () => {
    if (isCreatingConversation.current || !documentId) return;

    abortCurrentStream();

    isCreatingConversation.current = true;
    setError(null);
    // Only skip clearing messages if we have a pending (unsent) initial message
    if (
      !pendingInitialMessageRef.current ||
      pendingInitialMessageRef.current === 'sent'
    ) {
      // Mark as 'sent' to prevent initializeChat from re-triggering the
      // proofread branch when the initialMessage prop still persists
      pendingInitialMessageRef.current = 'sent';
      setMessages([]);
    }
    clearFileState();

    try {
      const conversation = await triggerCreateConversation({
        title: 'New Chat',
        documentId,
      }).unwrap();

      setConversationId(conversation.id);
      phaseRef.current = 'ready';

      trackProductEvent('Penny Conversation Created', {
        conversation_id: conversation.id,
        document_id: documentId,
      });

      setConversations((prev) => [conversation, ...prev]);
    } catch (err) {
      console.error('Error creating conversation:', err);
      setError(getApiErrorMessage(err, {resource: 'conversation'}));
      setIsLoading(false);
      phaseRef.current = 'idle';
    } finally {
      isCreatingConversation.current = false;
    }
  }, [documentId, triggerCreateConversation, clearFileState]);

  const fetchConversations = useCallback(async () => {
    if (conversationId || isCreatingConversation.current || !documentId) {
      return;
    }

    try {
      const data = await triggerListConversations({
        document_id: documentId,
      }).unwrap();

      const activeConversations = (data?.conversations || []).filter(
        (c) => c.status !== 'deleted'
      );
      setConversations(activeConversations);

      if (activeConversations.length > 0) {
        const documentConversations = activeConversations.filter(
          (c) => c.document_id === documentId
        );

        if (documentConversations.length === 0) {
          createNewConversation();
        } else {
          const conversationToLoad =
            documentConversations.find((c) => c.message_count > 0) ||
            documentConversations[0];
          setConversationId(conversationToLoad.id);
          loadConversation(conversationToLoad.id);
        }
      } else {
        createNewConversation();
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(getApiErrorMessage(err));
    }
  }, [
    conversationId,
    documentId,
    triggerListConversations,
    createNewConversation,
    loadConversation,
  ]);

  const deleteConversation = useCallback(
    async (id) => {
      try {
        const conversation = conversations.find((c) => c.id === id);
        await triggerDeleteConversation({conversationId: id}).unwrap();

        trackProductEvent('Penny Conversation Deleted', {
          conversation_id: id,
          document_id: documentId,
          message_count: conversation?.message_count || 0,
        });

        setConversations((prev) => prev.filter((c) => c.id !== id));

        if (id === conversationId) {
          setConversationId(null);
          setMessages([]);

          const remaining = conversations.filter(
            (c) => c.id !== id && c.status !== 'deleted'
          );
          if (remaining.length > 0) {
            loadConversation(remaining[0].id);
          } else {
            isCreatingConversation.current = false;
            createNewConversation();
          }
        }
      } catch (err) {
        console.error('Error deleting conversation:', err);
        setError(getApiErrorMessage(err, {resource: 'conversation'}));
        isCreatingConversation.current = false;
      }
    },
    [
      conversations,
      conversationId,
      documentId,
      triggerDeleteConversation,
      loadConversation,
      createNewConversation,
    ]
  );

  const renameConversation = useCallback(
    async (id, newTitle) => {
      if (!id || !newTitle.trim()) return;

      try {
        const oldTitle = conversations.find((c) => c.id === id)?.title;

        await triggerUpdateConversation({
          conversationId: id,
          title: newTitle.trim(),
        }).unwrap();

        trackProductEvent('Penny Conversation Renamed', {
          conversation_id: id,
          document_id: documentId,
          old_title: oldTitle,
          new_title: newTitle.trim(),
        });

        setConversations((prev) =>
          prev.map((c) => (c.id === id ? {...c, title: newTitle.trim()} : c))
        );
      } catch (err) {
        console.error('Error renaming conversation:', err);
        setError(getApiErrorMessage(err, {resource: 'conversation'}));
      }
    },
    [conversations, documentId, triggerUpdateConversation]
  );

  const clearCurrentConversation = useCallback(() => {
    if (!conversationId) return;
    setMessages([]);
    clearFileState();

    const conversation = conversations.find((c) => c.id === conversationId);
    if (conversation?.title?.includes('...')) {
      renameConversation(conversationId, 'New Chat');
    }
  }, [conversationId, conversations, clearFileState, renameConversation]);

  const sendMessage = useCallback(
    async (
      retryMsg = null,
      messageOverride = null,
      isInitialMessage = false
    ) => {
      const isRetry = !!retryMsg;
      const messageContent = isRetry ? retryMsg.content : messageOverride || '';
      if (!messageContent || !conversationId) return;

      const messageStartTime = Date.now();
      let thinkingStartTime = null;
      // activityLog: ordered list of {type:'thinking'|'tool', afterContent, ...} entries
      let activityLogCapture = [];
      let hasContentStarted = false;
      // Where in the content stream the next afterContent entry was inserted
      let lastChunkContent = '';
      // Prevents double-completing the same tool entry when both onChunk and onToolComplete fire
      let toolMarkedCompleteByChunk = false;
      // When thinking_complete fires, we capture the real duration but defer
      // flipping the UI to "Thought for N seconds" until the next event arrives.
      // This keeps the spinner visible through post-thinking gaps (tool round-trips,
      // backend latency between phases) instead of showing dead air.
      let pendingThinkingFinalization = null;

      const finalizeThinkingIfPending = () => {
        if (pendingThinkingFinalization === null) return;
        const {durationMs} = pendingThinkingFinalization;
        pendingThinkingFinalization = null;
        const last = activityLogCapture[activityLogCapture.length - 1];
        if (last?.type !== ACTIVITY_ENTRY_TYPE.THINKING || last.isComplete) {
          return;
        }
        const completedBlocks = (last.blocks || []).map((b) => ({
          ...b,
          isComplete: true,
        }));
        activityLogCapture = [
          ...activityLogCapture.slice(0, -1),
          {...last, blocks: completedBlocks, durationMs, isComplete: true},
        ];
      };

      const userMessage = isRetry
        ? retryMsg
        : {
            id: 'user-msg-' + Date.now(),
            content: messageContent,
            role: 'USER',
            timestamp: new Date().toISOString(),
            attachments: uploadedFiles.length > 0 ? uploadedFiles : null,
          };

      const _messages = isInitialMessage
        ? [userMessage]
        : [...messages, userMessage];

      if (!isRetry) {
        if (!isInitialMessage) {
          setMessages((prev) => [...prev, userMessage]);
        }

        trackProductEvent('Penny Message Sent', {
          conversation_id: conversationId,
          document_id: documentId,
          message_length: messageContent.length,
          has_attachments: uploadedFiles.length > 0,
          attachment_count: uploadedFiles.length,
          has_document_context:
            getAllEditors && Object.keys(getAllEditors()).length > 0,
          document_type: documentType || null,
        });

        clearFileState();
      }

      setIsLoading(true);
      setError(null);
      setFailedMessage(null);
      toolsExecutedRef.current = false;

      setCurrentStreamedMessage({
        id: 'streaming-' + Date.now(),
        content: '',
        activityLog: [],
        role: 'AGENT',
        timestamp: new Date().toISOString(),
      });

      const editors = getAllEditors ? getAllEditors() : null;

      try {
        await agentChatService.sendMessage(conversationId, messageContent, {
          callbacks: {
            onChunk: (chunk) => {
              finalizeThinkingIfPending();
              hasContentStarted = true;
              lastChunkContent = chunk.content || '';
              if (!toolMarkedCompleteByChunk) {
                toolMarkedCompleteByChunk = true;
                activityLogCapture = closeOpenToolEntry(activityLogCapture);
              }
              setCurrentStreamedMessage((prev) => ({
                ...prev,
                id: chunk.id || prev.id,
                content: chunk.content || '',
                activityLog: activityLogCapture,
              }));
            },
            onToolCall: (toolCall) => {
              finalizeThinkingIfPending();
              toolMarkedCompleteByChunk = false;
              const sectionName = getSectionName(
                toolCall.input?.sectionId,
                sectionsRef.current
              );
              const config = TOOL_CONFIG[toolCall.tool_name];
              const {activeLabel, completeLabel} = config?.getLabels(
                sectionName
              ) ?? {
                activeLabel: 'Working...',
                completeLabel: 'Done.',
              };
              activityLogCapture = [
                ...closeOpenToolEntry(activityLogCapture),
                {
                  type: ACTIVITY_ENTRY_TYPE.TOOL,
                  toolName: toolCall.tool_name,
                  activeLabel,
                  completeLabel,
                  isComplete: false,
                  afterContent: hasContentStarted,
                  contentAtEntry: hasContentStarted ? lastChunkContent : null,
                },
              ];
              setCurrentStreamedMessage((prevMsg) => ({
                ...prevMsg,
                activityLog: activityLogCapture,
              }));
            },
            onToolComplete: () => {
              toolsExecutedRef.current = true;
              // Don't close the active tool entry here — doing so runs in the
              // same synchronous batch as onToolCall, so React never renders
              // the "Editing..." state. Let the next stream event close it.
              if (editors) {
                setTimeout(() => refreshSuggestions(editors), 50);
              }
            },
            onThinkingChunk: (data) => {
              finalizeThinkingIfPending();
              if (!thinkingStartTime) {
                thinkingStartTime = Date.now();
              }
              const allBlocks = data.thinkingBlocks || [];
              const currentBlock = allBlocks[allBlocks.length - 1] || {
                content: '',
                isComplete: false,
              };
              const last = activityLogCapture[activityLogCapture.length - 1];
              if (
                !last ||
                last.type !== ACTIVITY_ENTRY_TYPE.THINKING ||
                last.isComplete
              ) {
                activityLogCapture = [
                  ...closeOpenToolEntry(activityLogCapture),
                  {
                    type: ACTIVITY_ENTRY_TYPE.THINKING,
                    blocks: [currentBlock],
                    durationMs: null,
                    isComplete: false,
                    afterContent: hasContentStarted,
                  },
                ];
              } else {
                activityLogCapture = [
                  ...activityLogCapture.slice(0, -1),
                  {...last, blocks: [currentBlock]},
                ];
              }
              setCurrentStreamedMessage((prev) => ({
                ...prev,
                id: data.id || prev.id,
                activityLog: activityLogCapture,
              }));
            },
            onThinkingComplete: () => {
              const phaseDuration = thinkingStartTime
                ? Date.now() - thinkingStartTime
                : 0;
              thinkingStartTime = null;
              pendingThinkingFinalization = {
                durationMs: phaseDuration > 0 ? phaseDuration : null,
              };
            },
            onError: (err) => {
              console.error('Error in stream:', err);
              setError(getApiErrorMessage(err));
              setCurrentStreamedMessage(null);
            },
            onComplete: async (complete) => {
              finalizeThinkingIfPending();
              trackProductEvent('Penny Response Received', {
                conversation_id: conversationId,
                document_id: documentId,
                response_length: complete.content.length,
                stream_duration: Date.now() - messageStartTime,
              });

              const finalActivityLog = activityLogCapture.map((entry) =>
                entry.isComplete ? entry : {...entry, isComplete: true}
              );
              setCurrentStreamedMessage(null);
              setFailedMessage(null);
              setMessages((prev) => [
                ...prev,
                {
                  id: complete.id,
                  content: complete.content,
                  role: 'AGENT',
                  timestamp: new Date().toISOString(),
                  ...(finalActivityLog.length > 0 && {
                    activityLog: finalActivityLog,
                  }),
                },
              ]);

              resortConversations(conversationId);

              if (_messages.length === 1) {
                setTimeout(async () => {
                  try {
                    const updated = await triggerGetConversation({
                      conversationId,
                    }).unwrap();
                    setConversations((prevConvos) =>
                      prevConvos.map((c) =>
                        c.id === conversationId ? updated : c
                      )
                    );
                  } catch (err) {
                    console.error('Error refreshing conversation title:', err);
                  }
                }, 2000);
              }
            },
          },
          attachments: userMessage.attachments,
          documentType,
          editors,
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('[AgentChat] Streaming request aborted');
        } else {
          console.error('Error sending message:', err);
          setError(getApiErrorMessage(err));
          setFailedMessage(userMessage);
        }
      } finally {
        setIsLoading(false);
        setCurrentStreamedMessage(null);
      }
    },
    [
      conversationId,
      messages,
      uploadedFiles,
      documentId,
      documentType,
      getAllEditors,
      refreshSuggestions,
      clearFileState,
      resortConversations,
      triggerGetConversation,
    ]
  );

  const handleFilesSelected = useCallback(
    async (files) => {
      if (!conversationId || !files.length) return;

      setIsUploadingFiles(true);
      setFileUploadError(null);

      try {
        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        const fileTypes = [...new Set(files.map((file) => file.type))];

        const result = await agentChatService.uploadFiles(
          conversationId,
          files
        );

        if (result.files?.length > 0) {
          trackProductEvent('Penny File Uploaded', {
            conversation_id: conversationId,
            document_id: documentId,
            file_count: result.files.length,
            total_size: totalSize,
            file_types: fileTypes.join(', '),
            success: true,
          });
          setUploadedFiles((prev) => [...prev, ...result.files]);
        }

        if (result.errors?.length > 0) {
          trackProductEvent('Penny File Upload Failed', {
            conversation_id: conversationId,
            document_id: documentId,
            error_count: result.errors.length,
            errors: result.errors.map((e) => e.error).join(', '),
          });
          setFileUploadError(
            `Some files failed to upload: ${result.errors.map((e) => e.error).join(', ')}`
          );
        }
      } catch (err) {
        console.error('Error uploading files:', err);
        setFileUploadError('Failed to upload files. Please try again.');
      } finally {
        setIsUploadingFiles(false);
      }
    },
    [conversationId, documentId]
  );

  const handleRemoveFile = useCallback((fileId) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  }, []);

  const initializeChat = useCallback(
    (open, initialMessage) => {
      if (!open) {
        phaseRef.current = 'idle';
        pendingInitialMessageRef.current = null;
        setMessages([]);
        return;
      }

      // Block re-entry while initializing
      if (phaseRef.current === 'initializing') return;

      // Proofread flow: only when not already pending and no active stream
      if (
        initialMessage &&
        !pendingInitialMessageRef.current &&
        !currentStreamedMessage
      ) {
        phaseRef.current = 'initializing';
        pendingInitialMessageRef.current = initialMessage;
        setMessages([
          {
            id: 'initial-user-msg',
            content: initialMessage,
            role: 'USER',
            timestamp: new Date().toISOString(),
          },
        ]);
        setIsLoading(true);
        if (documentId) {
          triggerListConversations({document_id: documentId})
            .unwrap()
            .then((data) => {
              const activeConversations = (data?.conversations || []).filter(
                (c) => c.status !== 'deleted'
              );
              setConversations(activeConversations);
            })
            .catch((err) =>
              console.error('Error fetching conversations:', err)
            );
        }
        createNewConversation();
        return;
      }

      // Normal flow: only from idle
      if (phaseRef.current === 'idle') {
        phaseRef.current = 'initializing';
        if (conversationId) {
          loadConversation(conversationId);
        } else {
          fetchConversations();
        }
      }
    },
    [
      conversationId,
      documentId,
      currentStreamedMessage,
      triggerListConversations,
      createNewConversation,
      loadConversation,
      fetchConversations,
    ]
  );

  const sendInitialMessage = useCallback(() => {
    if (
      pendingInitialMessageRef.current &&
      pendingInitialMessageRef.current !== 'sent' &&
      conversationId &&
      phaseRef.current === 'ready' &&
      !isCreatingConversation.current
    ) {
      const msg = pendingInitialMessageRef.current;
      // Mark as sent (not null) so initializeChat won't re-trigger while
      // the same initialMessage prop persists on the screen
      pendingInitialMessageRef.current = 'sent';
      sendMessage(null, msg, true);
    }
  }, [conversationId, sendMessage]);

  const resetForUnmount = useCallback(() => {
    if (!currentStreamedMessageRef.current) {
      phaseRef.current = 'idle';
      pendingInitialMessageRef.current = null;
    }
  }, []);

  const value = {
    // Conversation state
    messages,
    setMessages,
    conversationId,
    conversations,
    error,
    isLoading,
    isLoadingConversations,
    failedMessage,

    // Streaming state
    currentStreamedMessage,

    // File state
    uploadedFiles,
    isUploadingFiles,
    fileUploadError,
    setFileUploadError,

    // Refs
    isCreatingConversation,

    // Actions
    sendMessage,
    fetchConversations,
    createNewConversation,
    loadConversation,
    deleteConversation,
    renameConversation,
    clearCurrentConversation,
    handleFilesSelected,
    handleRemoveFile,
    initializeChat,
    sendInitialMessage,
    resetForUnmount,
  };

  return (
    <AgentChatContext.Provider value={value}>
      {children}
    </AgentChatContext.Provider>
  );
};

AgentChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
  getAllEditors: PropTypes.func,
  documentType: PropTypes.string,
};

export const useAgentChat = () => {
  const context = useContext(AgentChatContext);
  if (!context) {
    throw new Error('useAgentChat must be used within an AgentChatProvider');
  }
  return context;
};
